import { useCallback, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ChatSession } from '../../../domain/chat/models/ChatSession';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { acquireApiAccessTokenAsync, acquireNexusApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { loadChatMessagesHandler } from '../../../application/chat-messages/handlers/LoadChatMessagesHandler';
import { deleteChatSessionHandler } from '../../../application/chat-sessions/handlers/DeleteChatSessionHandler';
import { createChatSessionHandler } from '../../../application/chat-sessions/handlers/CreateChatSessionHandler';
import { downloadChatSessionHandler } from '../../../application/chat-sessions/handlers/DownloadChatSessionHandler';
import { updateChatSessionHandler } from '../../../application/chat-sessions/handlers/UpdateChatSessionHandler';
import { Agent } from '../../../domain/agents/models/Agent';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { downloadChatSessionAsText } from '../../utils/downloadChatSessionDocument';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setHasMoreMessages, setIsLoadingMessages, setLoadedMessageCount, setSelectedSessionId } from '../../../redux/features/chatUi/chatUiSlice';
import { setDocuments } from '../../../redux/features/chatDocuments/chatDocumentsSlice';
import { clearMessages, setMessages } from '../../../redux/features/chatMessages/chatMessagesSlice';
import { CHAT_MESSAGES_PAGE_SIZE, supportsMessagePagination } from '../../../application/chat-messages/chatMessagesPaging';
import { removeSession, removeSessions, upsertSession } from '../../../redux/features/chatSessions/chatSessionsSlice';
import {
    ChatListViewModel,
    selectChatListViewModel,
} from '../../../domain/selectors/view-models/chat/selectChatListViewModel';
import { selectActiveChatSessionCount } from '../../../domain/selectors/base/chat/selectActiveChatSessionCount';
import { alerts } from '../../../shared/ui/Alerts';

export interface ChatListVm extends ChatListViewModel {
    hasSessions: boolean;
    activeChatCount: number;
    allowedChatCount: number | null;
    isMaxChatsReached: boolean;
    manageChatItems: ManageChatItemVm[];
    createSession: () => Promise<void>;
    selectSession: (sessionId: string) => void;
    deleteSession: (sessionId: string) => Promise<void>;
    renameSession: (sessionId: string, title: string) => Promise<void>;
    downloadSession: (sessionId: string) => Promise<void>;

    // Multi-select / bulk-delete
    selectedIds: Set<string>;
    isDeletingMultiple: boolean;
    toggleSelectSession: (sessionId: string) => void;
    setSelectedSessions: (sessionIds: string[]) => void;
    clearSelection: () => void;
    deleteSelectedSessions: () => Promise<boolean>;
}

export interface ManageChatItemVm {
    sessionId: string;
    title: string;
    providerType: ChatProviderType;
    providerLabel: string;
    agentId: string | null;
    agentName: string;
    modifiedDate: number;
    modifiedText: string;
    isArchived: boolean;
    isCurrent: boolean;
}

export type ManageChatsTabValue = 'active' | 'archived';
export type ProviderFilterValue = 'all' | 'progpt' | 'nexus' | 'assistants';
export type SortValue = 'recent' | 'oldest' | 'title';

export interface UseManageChatsDialogOptions {
    items: ManageChatItemVm[];
    selectedIds: Set<string>;
    setSelectedSessions: (sessionIds: string[]) => void;
    clearSelection: () => void;
}

export interface UseManageChatsDialogVm {
    selectedTab: ManageChatsTabValue;
    setSelectedTab: (value: ManageChatsTabValue) => void;
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    providerFilter: ProviderFilterValue;
    setProviderFilter: (value: ProviderFilterValue) => void;
    agentFilter: string;
    setAgentFilter: (value: string) => void;
    sortBy: SortValue;
    setSortBy: (value: SortValue) => void;
    agentOptions: Array<[string, string]>;
    filteredItems: ManageChatItemVm[];
    visibleSelectableIds: string[];
    headerCheckboxState: boolean | 'mixed';
    handleHeaderCheckboxChange: (checked: boolean) => void;
}

interface DeleteChatSessionsResult {
    deletedSessionIds: string[];
    failedSessionIds: string[];
}

const toTimestampMs = (session: ChatSession): number => session.modifiedDate ?? session.createdDate ?? 0;

const createChatSessionToastMessages = {
    pending: 'Creating chat...',
    success: 'Chat created successfully!',
    error: 'Failed to create a new chat. Please try again.',
};

const formatSessionTimestamp = (session: ChatSession): string => {
    const ts = toTimestampMs(session);
    return ts ? new Date(ts).toLocaleString() : 'No activity';
};

const toProviderLabel = (providerType: ChatProviderType): string => {
    switch (providerType) {
        case ChatProviderType.ProGPT:
            return 'ProGPT';
        case ChatProviderType.Nexus:
            return 'Nexus';
        case ChatProviderType.Assistants:
            return 'Archived';
        default:
            return 'Unknown';
    }
};

const resolveAgentName = (session: ChatSession, agentsById: Record<string, Agent>): string => {
    const resolvedAgent = resolveSessionAgent(session, agentsById);
    return resolvedAgent?.name ?? 'Unknown agent';
};

export const useChatList = (): ChatListVm => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const providerServices = useProviderServices(); // get services to pass to handlers to make api calls
    const { instance } = useMsal();

    // selectors to get data and shape it for the UI
    const messagesBySessionId = useAppSelector((state) => state.chatMessages.bySessionId);
    const selectedSessionId = useAppSelector((state) => state.chatUi.selectedSessionId);
    const sessionsById = useAppSelector((state) => state.chatSessions.byId);
    const sessionIds = useAppSelector((state) => state.chatSessions.allIds);
    const agentsById = useAppSelector((state) => state.agents.byId);
    const activeChatCount = useAppSelector(selectActiveChatSessionCount);

    const allowedChatCount = useMemo(() => {
        const parsedValue = Number(process.env.REACT_APP_CHAT_SESSION_COUNT ?? '');

        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
            return null;
        }

        return parsedValue;
    }, []);

    // selector to get the chat list items with resolved featureset and agent data, so the component can just render it without additional logic
    const { primaryItems, archivedItems } = useAppSelector(selectChatListViewModel);

    // Multi-select state — local to this hook, not in Redux, since it's transient UI state
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);
    const isMaxChatsReached = allowedChatCount !== null && activeChatCount >= allowedChatCount;

    const toggleSelectSession = useCallback((sessionId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(sessionId)) {
                next.delete(sessionId);
            } else {
                next.add(sessionId);
            }
            return next;
        });
    }, []);

    const setSelectedSessions = useCallback((sessionIds: string[]) => {
        setSelectedIds(new Set(sessionIds));
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const manageChatItems = useMemo(() => {
        return sessionIds
            .map((id) => sessionsById[id])
            .filter(Boolean)
            .map(
                (session): ManageChatItemVm => ({
                    sessionId: session.id,
                    title: session.title,
                    providerType: session.providerType,
                    providerLabel: toProviderLabel(session.providerType),
                    agentId: session.agentId ?? null,
                    agentName: resolveAgentName(session, agentsById),
                    modifiedDate: toTimestampMs(session),
                    modifiedText: formatSessionTimestamp(session),
                    isArchived: session.providerType === ChatProviderType.Assistants,
                    isCurrent: session.id === selectedSessionId,
                }),
            )
            .sort((left, right) => right.modifiedDate - left.modifiedDate);
    }, [agentsById, selectedSessionId, sessionIds, sessionsById]);

    // helper function to load messages for a session if they are not already loaded, used when selecting a session to ensure messages are available for the chat window
    const loadSessionMessagesIfNeededAsync = useCallback(
        async (sessionId: string): Promise<void> => {
            if (messagesBySessionId[sessionId] !== undefined) {
                return;
            }

            const session = sessionsById[sessionId];
            if (!session) {
                return;
            }

            dispatch(
                setIsLoadingMessages({
                    sessionId,
                    value: true,
                }),
            );

            try {
                const accessToken = session.providerType === ChatProviderType.Nexus
                    ? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
                    : await acquireApiAccessTokenAsync({ msalInstance: instance });

                // call handler to load messages, passing in token, any state data, and provider services to make the call
                const messages = await loadChatMessagesHandler({
                    session,
                    accessToken,
                    providerServices,
                    skip: 0,
                    count: CHAT_MESSAGES_PAGE_SIZE,
                });
    		// store loaded messages in redux so they are available for the chat window and other components
                dispatch(setMessages({ sessionId, messages }));
                dispatch(setHasMoreMessages({ sessionId, value: supportsMessagePagination(session.providerType) && messages.length === CHAT_MESSAGES_PAGE_SIZE }));
                dispatch(setLoadedMessageCount({ sessionId, value: messages.length }));
            } catch {
                alerts.error('Failed to load messages. Please try again.');
            } finally {
                dispatch(
                    setIsLoadingMessages({
                        sessionId,
                        value: false,
                    }),
                );
            }
        },
        [dispatch, instance, messagesBySessionId, providerServices, sessionsById],
    );

    // callback for when a session is selected in the UI, sets the selected session in state and loads messages if needed
    const selectSession = useCallback(
        (sessionId: string): void => {
            dispatch(setSelectedSessionId(sessionId));
            navigate(`/chat/${sessionId}`);

            // void so that selectSession updates the selected chat and route immediately without waiting for messages to load
            void loadSessionMessagesIfNeededAsync(sessionId);
        },
        [dispatch, loadSessionMessagesIfNeededAsync, navigate],
    );

    // enforce max count when creating a new ProGPT session from the main "new chat" button, but not when auto-creating after deleting the last session, to avoid a dead-end where the user can't create any sessions due to max count but also has no sessions to delete
    const createProGPTSessionAsync = useCallback(
        async (enforceMaxCount: boolean): Promise<string | null> => {
            if (enforceMaxCount && allowedChatCount !== null && activeChatCount >= allowedChatCount) {
                alerts.error(
                    `You have reached the maximum limit of ${allowedChatCount} active chat sessions. Please delete an existing chat to create a new one.`,
                );
                return null;
            }

            try {
                const accessToken = await acquireApiAccessTokenAsync({
                    msalInstance: instance,
                });

                const createdSession = await alerts.promise(
                    createChatSessionHandler({
                        request: {
                            providerType: ChatProviderType.ProGPT,
                            title: `New ProGPT Chat`,
                        },
                        accessToken,
                        providerServices,
                    }),
                    createChatSessionToastMessages,
                );

                // add session to the start of the list, select it, and load messages so the chat window is ready to go when navigated to
                dispatch(upsertSession(createdSession.session));
                dispatch(
                    setDocuments({
                        sessionId: createdSession.session.id,
                        documents: createdSession.documents,
                    }),
                );
                dispatch(setSelectedSessionId(createdSession.session.id));
                navigate(`/chat/${createdSession.session.id}`);
                void loadSessionMessagesIfNeededAsync(createdSession.session.id);
                return createdSession.session.id;
            } catch {
                return null;
            }
        },
        [
            activeChatCount,
            allowedChatCount,
            dispatch,
            instance,
            loadSessionMessagesIfNeededAsync,
            navigate,
            providerServices,
        ],
    );

    // callback for creating a new ProGPT chat session, calls the create handler and then selects the new session
    const createSession = useCallback(async (): Promise<void> => {
        await createProGPTSessionAsync(true);
    }, [createProGPTSessionAsync]);

    // callback for deleting a session, calls delete handler, removes from state, and selects the next available session or creates a new one if there are no remaining sessions
    const deleteSession = useCallback(
        async (sessionId: string): Promise<void> => {
            const session = sessionsById[sessionId];
            if (!session) {
                return;
            }

            const accessToken = session.providerType === ChatProviderType.Nexus
                ? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
                : await acquireApiAccessTokenAsync({ msalInstance: instance });

            try {
                await deleteChatSessionHandler({
                    session,
                    accessToken,
                    providerServices,
                });
            } catch {
                alerts.error('Failed to delete chat. Please try again.');
                return;
            }

            // remove session and messages from state
            dispatch(removeSession(sessionId));
            dispatch(clearMessages(sessionId));

            // select the next available session, prioritizing the one above the deleted session in the list, or create a new one if there are no remaining sessions
            const remainingSessionIds = sessionIds.filter((id) => id !== sessionId);
            const nextSessionId =
                selectedSessionId !== null && remainingSessionIds.includes(selectedSessionId)
                    ? selectedSessionId
                    : (remainingSessionIds[0] ?? null);

            if (nextSessionId) {
                dispatch(setSelectedSessionId(nextSessionId));
                navigate(`/chat/${nextSessionId}`);
                void loadSessionMessagesIfNeededAsync(nextSessionId);
                return;
            }

            dispatch(setSelectedSessionId(null));
            await createProGPTSessionAsync(false);
        },
        [
            createProGPTSessionAsync,
            dispatch,
            instance,
            loadSessionMessagesIfNeededAsync,
            navigate,
            providerServices,
            selectedSessionId,
            sessionIds,
            sessionsById,
        ],
    );

    const renameSession = useCallback(
        async (sessionId: string, title: string): Promise<void> => {
            const session = sessionsById[sessionId];
            if (!session) {
                return;
            }

            const accessToken = session.providerType === ChatProviderType.Nexus
                ? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
                : await acquireApiAccessTokenAsync({ msalInstance: instance });

            try {
                const updatedSession = await updateChatSessionHandler({
                    command: {
                        sessionId,
                        title,
                        isTitleGenerated: true,
                    },
                    session,
                    accessToken,
                    providerServices,
                    resolvedAgent: resolveSessionAgent(session, agentsById),
                });

                dispatch(upsertSession(updatedSession.session));
                dispatch(
                    setDocuments({
                        sessionId: updatedSession.session.id,
                        documents: updatedSession.documents,
                    }),
                );
            } catch {
                alerts.error('Failed to rename chat. Please try again.');
            }
        },
        [dispatch, instance, providerServices, sessionsById, agentsById],
    );

    const downloadSession = useCallback(
        async (sessionId: string): Promise<void> => {
            const session = sessionsById[sessionId];
            if (!session) {
                return;
            }

            const accessToken = session.providerType === ChatProviderType.Nexus
                ? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
                : await acquireApiAccessTokenAsync({ msalInstance: instance });

            try {
                const sessionData = await downloadChatSessionHandler({
                    session,
                    accessToken,
                    providerServices,
                });

                downloadChatSessionAsText(sessionData, session.title);
            } catch {
                alerts.error('Failed to download chat. Please try again.');
            }
        },
        [instance, providerServices, sessionsById],
    );

    // callback for bulk-deleting all selected sessions via the multi-select UI
    const deleteSelectedSessions = useCallback(async (): Promise<boolean> => {
        if (selectedIds.size === 0) return false;

        setIsDeletingMultiple(true);

        try {
            const sessionsToDelete = Array.from(selectedIds)
                .map((id) => sessionsById[id])
                .filter(Boolean);

            if (sessionsToDelete.length === 0) return false;

            const hasNexusSessions = sessionsToDelete.some((s) => s.providerType === ChatProviderType.Nexus);
            const accessToken = await acquireApiAccessTokenAsync({ msalInstance: instance });
            const nexusAccessToken = hasNexusSessions
                ? await acquireNexusApiAccessTokenAsync({ msalInstance: instance })
                : '';

            const deleteResult = await alerts.promise<DeleteChatSessionsResult>(
                (async (): Promise<DeleteChatSessionsResult> => {
                    const aggregateResult: DeleteChatSessionsResult = {
                        deletedSessionIds: [],
                        failedSessionIds: [],
                    };

                    for (const session of sessionsToDelete) {
                        const token = session.providerType === ChatProviderType.Nexus ? nexusAccessToken : accessToken;
                        try {
                            await deleteChatSessionHandler({
                                session,
                                accessToken: token,
                                providerServices,
                            });
                            aggregateResult.deletedSessionIds.push(session.id);
                        } catch {
                            aggregateResult.failedSessionIds.push(session.id);
                        }
                    }

                    return aggregateResult;
                })(),
                {
                    pending: 'Deleting chats…',
                    success: {
                        render({ data }) {
                            const deletedCount = data?.deletedSessionIds.length ?? 0;
                            const failedCount = data?.failedSessionIds.length ?? 0;

                            if (failedCount > 0) {
                                return `Deleted ${deletedCount} chats. ${failedCount} failed to delete.`;
                            }

                            return deletedCount === 1 ? 'Chat deleted successfully!' : 'Chats deleted successfully!';
                        },
                    },
                    error: 'Failed to delete chats.',
                },
            );

            const idsToRemove = deleteResult.deletedSessionIds;

            if (idsToRemove.length === 0) {
                return false;
            }

            const removedIdSet = new Set(idsToRemove);
            dispatch(removeSessions(idsToRemove));

            for (const id of idsToRemove) {
                dispatch(clearMessages(id));
            }

            setSelectedSessions(deleteResult.failedSessionIds);

            const remainingIds = sessionIds.filter((id) => !removedIdSet.has(id));
            const nextSelectedSessionId =
                selectedSessionId !== null && remainingIds.includes(selectedSessionId)
                    ? selectedSessionId
                    : (remainingIds[0] ?? null);

            if (nextSelectedSessionId) {
                dispatch(setSelectedSessionId(nextSelectedSessionId));
                navigate(`/chat/${nextSelectedSessionId}`);
                void loadSessionMessagesIfNeededAsync(nextSelectedSessionId);
                return true;
            }

            dispatch(setSelectedSessionId(null));
            return (await createProGPTSessionAsync(false)) !== null;
        } catch {
            return false;
        } finally {
            setIsDeletingMultiple(false);
        }
    }, [
        createProGPTSessionAsync,
        dispatch,
        instance,
        loadSessionMessagesIfNeededAsync,
        navigate,
        providerServices,
        selectedIds,
        selectedSessionId,
        setSelectedSessions,
        sessionIds,
        sessionsById,
    ]);

    // return all items that are needed by the component to render the list, as well as callbacks for actions that can be taken on the list (selecting, creating, deleting, renaming, downloading sessions)
    return {
        primaryItems,
        archivedItems,
        hasSessions: primaryItems.length > 0 || archivedItems.length > 0,
        activeChatCount,
        allowedChatCount,
        isMaxChatsReached,
        manageChatItems,
        createSession,
        selectSession,
        deleteSession,
        renameSession,
        downloadSession,
        selectedIds,
        isDeletingMultiple,
        toggleSelectSession,
        setSelectedSessions,
        clearSelection,
        deleteSelectedSessions,
    };
};

export default useChatList;
